/**
 * Drag-to-reorder table — US-A02 AC4, US-A03 AC4, US-A11 AC5, W-091.
 *
 * `sort_order` is an int column on every orderable table (docs/03 §4.2). On
 * drop we renumber the whole visible page from 0 and hand the caller the new
 * order; the caller persists it (a single `updateMany`) and the app reflects it.
 *
 * Native HTML5 drag and drop rather than a library: one interaction, four
 * screens, no need for a dependency.
 */
import { Table } from 'antd'
import type { TableProps } from 'antd'
import { useState } from 'react'
import type { HTMLAttributes } from 'react'

export interface OrderableRow {
  id: string
  sort_order: number
}

export interface OrderableTableProps<T extends OrderableRow> extends TableProps<T> {
  dataSource?: T[]
  /** Receives the rows in their new order, already renumbered. */
  onReorder: (ordered: T[]) => void
  /** Disabled while a save is in flight, or when the role may not edit. */
  reorderDisabled?: boolean
}

interface DraggableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  'data-row-key'?: string
}

export function OrderableTable<T extends OrderableRow>({
  dataSource,
  onReorder,
  reorderDisabled = false,
  ...tableProps
}: OrderableTableProps<T>) {
  const [draggedKey, setDraggedKey] = useState<string | null>(null)

  const handleDrop = (targetKey: string) => {
    if (!dataSource || !draggedKey || draggedKey === targetKey) return
    const from = dataSource.findIndex(row => row.id === draggedKey)
    const to = dataSource.findIndex(row => row.id === targetKey)
    if (from < 0 || to < 0) return

    const next = [...dataSource]
    const [moved] = next.splice(from, 1)
    if (!moved) return
    next.splice(to, 0, moved)
    onReorder(next.map((row, index) => ({ ...row, sort_order: index })))
    setDraggedKey(null)
  }

  const Row = (props: DraggableRowProps) => {
    const key = props['data-row-key']
    if (reorderDisabled || !key) return <tr {...props} />
    return (
      <tr
        {...props}
        draggable
        onDragStart={() => setDraggedKey(key)}
        onDragOver={event => event.preventDefault()}
        onDrop={() => handleDrop(key)}
        style={{ ...props.style, cursor: 'grab' }}
      />
    )
  }

  return (
    <Table<T>
      {...tableProps}
      rowKey="id"
      dataSource={dataSource}
      components={{ body: { row: Row } }}
    />
  )
}
